import * as THREE from 'three';
import { type CameraMode } from '../core/settings';
import { StepSnapSmoother } from './step-snap-smoother';

// ---------------------------------------------------------------------------
// Camera system with First-Person (FPP) and Third-Person (TPP) modes
// ---------------------------------------------------------------------------

const DEG89 = (89 * Math.PI) / 180; // pitch clamp
const DEFAULT_SENSITIVITY = 0.002;
const EYE_HEIGHT = 1.6; // metres above player origin

// TPP defaults
const TPP_TRANSITION_SPEED = 8; // lerp speed (higher = faster transition)

// Raycaster for TPP wall collision
const _ray = new THREE.Raycaster();
_ray.near = 0.1;
_ray.far = 10;
const _rayOrigin = new THREE.Vector3();
const _rayDir = new THREE.Vector3();

// Module-level reusable vector for getDirection()
const _dirOut = new THREE.Vector3();

/**
 * Return true if the given object should block the TPP camera.
 * Excludes: local player body/weapon, effect/decal meshes, PVS-hidden meshes,
 * non-mesh objects, and transparent materials (smoke, glass, vegetation cards).
 * Walks up the parent chain so a child marked via a group still gets filtered.
 */
export function isCameraBlocker(obj: THREE.Object3D): boolean {
  if (!obj.visible) return false;
  // Only meshes can be camera blockers — lights, groups, helpers, etc. cannot.
  if (!(obj as THREE.Mesh).isMesh) return false;

  // Walk up to catch flags set on a parent wrapper (e.g. localPlayerBody group).
  for (let cur: THREE.Object3D | null = obj; cur; cur = cur.parent) {
    if (!cur.visible) return false;
    const ud = cur.userData;
    if (!ud) continue;
    if (ud.isLocalPlayer) return false;
    if (ud.isEffect) return false;
    if (ud.batchedAway) return false;
    if (ud.isDecal) return false;
    if (ud.noCameraCollide) return false;
    if (ud.isTracer) return false;
  }

  const mesh = obj as THREE.Mesh;
  const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
  if (mat && (mat as THREE.Material).transparent) return false;
  return true;
}

export class FPSCamera {
  readonly camera: THREE.PerspectiveCamera;

  private sensitivity: number;
  private yaw = 0;   // radians, around Y axis
  private pitch = 0;  // radians, around X axis
  private locked = false;

  // Camera mode
  private mode: CameraMode = 'fpp';
  private tppDistance = 3.5;
  private tppHeight = 1.2;
  private tppShoulder = 0.5; // positive = right shoulder

  // Smooth transition state
  private currentOffset = new THREE.Vector3(0, 0, 0);
  private targetOffset = new THREE.Vector3(0, 0, 0);

  // Reusable scratch objects to avoid per-frame allocations
  private _euler = new THREE.Euler(0, 0, 0, 'YXZ');
  private _eyePos = new THREE.Vector3();
  private _forward = new THREE.Vector3();
  private _shoulderEuler = new THREE.Euler(0, 0, 0, 'YXZ');
  private _shoulderDir = new THREE.Vector3();
  private _desiredOffset = new THREE.Vector3();
  private _clampedOffset = new THREE.Vector3();

  // Collision scene reference (set externally for TPP raycasting)
  private collisionScene: THREE.Scene | null = null;

  // Visual-only recoil overlay (does NOT affect server-authoritative aim).
  // Overwritten each frame by RecoilKickSystem; layered into the final euler
  // in updateFPP/updateTPP.
  private visualRecoilPitch = 0;
  private visualRecoilRoll = 0;

  // Step-snap visual smoother — eases the camera Y over 5 ticks after a
  // server step-up so stair traversal doesn't pop. Pass-through outside the
  // smoothing window (see step-snap-smoother.ts).
  private stepSnapSmoother = new StepSnapSmoother();
  private _smoothedPos = new THREE.Vector3();

  constructor(sensitivity: number = DEFAULT_SENSITIVITY) {
    this.sensitivity = sensitivity;

    this.camera = new THREE.PerspectiveCamera(
      90,                                   // FOV
      window.innerWidth / window.innerHeight, // aspect
      0.1,                                  // near
      1000,                                 // far
    );

    // Bind handlers so we can remove them later
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onPointerLockChange = this._onPointerLockChange.bind(this);
    this._onClick = this._onClick.bind(this);
    document.addEventListener('pointerlockchange', this._onPointerLockChange);
    document.addEventListener('click', this._onClick);
    document.addEventListener('mousemove', this._onMouseMove);
    // NOTE: resize is handled centrally in game.ts
  }

  // ---- Public API ---------------------------------------------------------

  /** Request pointer lock on the game canvas. */
  lock(): void {
    const canvas = document.getElementById('game-canvas');
    if (canvas && !this.locked) {
      try {
        canvas.requestPointerLock();
      } catch {
        // Pointer lock requires a user gesture; silently ignore if not available
      }
    }
  }

  /** Release pointer lock. */
  unlock(): void {
    if (this.locked) {
      document.exitPointerLock();
    }
  }

  /** Whether pointer lock is active. */
  get isLocked(): boolean {
    return this.locked;
  }

  /** Current yaw in radians. */
  getYaw(): number {
    return this.yaw;
  }

  /** Current pitch in radians. */
  getPitch(): number {
    return this.pitch;
  }

  /** Set yaw and pitch directly (e.g. from network state). */
  setAngles(yaw: number, pitch: number): void {
    this.yaw = yaw;
    this.pitch = Math.max(-DEG89, Math.min(DEG89, pitch));
  }

  /**
   * Set a transient visual-only pitch+roll overlay applied on top of yaw/pitch
   * each frame. RecoilKickSystem drives these via spring dynamics for a more
   * organic camera kick. Does NOT affect server-authoritative aim direction.
   */
  setVisualRecoilOffset(pitchOffset: number, rollOffset: number): void {
    this.visualRecoilPitch = pitchOffset;
    this.visualRecoilRoll = rollOffset;
  }

  /** Current camera mode. */
  getMode(): CameraMode {
    return this.mode;
  }

  /** Set camera mode (FPP or TPP). */
  setMode(mode: CameraMode): void {
    this.mode = mode;
    if (mode === 'fpp') {
      this.targetOffset.set(0, 0, 0);
    }
  }

  /** Set TPP parameters. */
  setTPPParams(distance: number, height: number, shoulder: number): void {
    this.tppDistance = distance;
    this.tppHeight = height;
    this.tppShoulder = shoulder;
  }

  /** Set the scene used for TPP camera collision raycasting. */
  setCollisionScene(scene: THREE.Scene): void {
    this.collisionScene = scene;
  }

  /** Update camera to follow a player position with current look angles.
   *  @param eyeHeight Override eye height (default: EYE_HEIGHT). Use for smooth crouch/slide transitions.
   */
  update(position: THREE.Vector3, yaw?: number, pitch?: number, eyeHeight?: number, dt?: number): void {
    if (yaw !== undefined) this.yaw = yaw;
    if (pitch !== undefined) this.pitch = Math.max(-DEG89, Math.min(DEG89, pitch));

    // Hide server step-up snaps: smooth the Y component over 5 ticks while
    // leaving X/Z untouched. Pass-through outside step-up windows so this
    // never fights other systems (gravity, jump, mantle, respawn).
    const smoothedY = this.stepSnapSmoother.update(position.y);
    const smoothedPos = this._smoothedPos.set(position.x, smoothedY, position.z);

    if (this.mode === 'fpp') {
      this.updateFPP(smoothedPos, eyeHeight, dt);
    } else {
      this.updateTPP(smoothedPos, eyeHeight, dt);
    }
  }

  /** Reseed the step-snap smoother (e.g. on respawn or map load). */
  resetStepSnap(y: number): void {
    this.stepSnapSmoother.reset(y);
  }

  /** Get the direction the camera is facing as a unit vector.
   *  NOTE: Returns a shared scratch vector — callers must .clone() if they
   *  need to store the result beyond the current frame. */
  getDirection(): THREE.Vector3 {
    return _dirOut.set(0, 0, -1).applyQuaternion(this.camera.quaternion);
  }

  /** Update mouse sensitivity at runtime. */
  setSensitivity(value: number): void {
    this.sensitivity = value;
  }

  /** Update camera aspect ratio. Called from the central resize handler. */
  handleResize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /** Clean up event listeners. */
  dispose(): void {
    document.removeEventListener('pointerlockchange', this._onPointerLockChange);
    document.removeEventListener('click', this._onClick);
    document.removeEventListener('mousemove', this._onMouseMove);
  }

  // ---- Camera mode updates (private) ------------------------------------

  private updateFPP(position: THREE.Vector3, eyeHeight?: number, dt?: number): void {
    // Lerp offset back to zero for smooth transition from TPP (framerate-independent)
    const fppLerp = dt ? 1 - Math.exp(-10 * dt) : 0.2;
    this.currentOffset.lerp(this.targetOffset, fppLerp);

    // Position camera at player eye height (overridable for crouch/slide)
    const eh = eyeHeight ?? EYE_HEIGHT;
    this.camera.position.set(
      position.x + this.currentOffset.x,
      position.y + eh + this.currentOffset.y,
      position.z + this.currentOffset.z,
    );

    // Build rotation from yaw + pitch + transient visual recoil overlay
    // (RecoilKickSystem drives pitch/roll each frame; roll is visual-only).
    this._euler.set(
      this.pitch + this.visualRecoilPitch,
      this.yaw,
      this.visualRecoilRoll,
      'YXZ',
    );
    this.camera.quaternion.setFromEuler(this._euler);
  }

  private updateTPP(position: THREE.Vector3, eyeHeight?: number, dt?: number): void {
    const eh = eyeHeight ?? EYE_HEIGHT;
    // Eye position (where the player's eyes are) — reuse scratch vector
    const eyePos = this._eyePos.set(position.x, position.y + eh, position.z);

    // Build rotation from yaw + pitch (reuse scratch euler)
    this._euler.set(this.pitch, this.yaw, 0, 'YXZ');
    this.camera.quaternion.setFromEuler(this._euler);

    // Calculate desired TPP offset: behind + above + shoulder
    // "Behind" is opposite to look direction
    const forward = this._forward.set(0, 0, -1).applyEuler(this._euler);
    this._shoulderEuler.set(0, this.yaw, 0, 'YXZ');
    const shoulderDir = this._shoulderDir.set(1, 0, 0).applyEuler(this._shoulderEuler);

    const desiredOffset = this._desiredOffset.set(0, 0, 0);
    // Behind player (opposite of forward)
    desiredOffset.addScaledVector(forward, -this.tppDistance);
    // Above player
    desiredOffset.y += this.tppHeight;
    // Shoulder offset
    desiredOffset.addScaledVector(shoulderDir, this.tppShoulder);

    // Target position
    this.targetOffset.copy(desiredOffset);

    // Initialise the offset direction up front so later code can use it even
    // when no collision scene is set, or when the raycast finds no hits.
    const desiredLen = desiredOffset.length();
    if (desiredLen > 1e-4) {
      _rayDir.copy(desiredOffset).divideScalar(desiredLen);
    } else {
      _rayDir.set(0, 0, 1);
    }

    // Collision check: raycast from eye toward desired camera position. We must
    // filter out the local player's own body/weapon (otherwise the camera snaps
    // onto the player), effect/decal meshes, PVS-hidden meshes, and any
    // transparent material — none of which should block the camera.
    let finalDistance = desiredLen;
    if (this.collisionScene && desiredLen > 1e-4) {
      _rayOrigin.copy(eyePos);
      _ray.set(_rayOrigin, _rayDir);
      _ray.far = desiredLen;

      const intersects = _ray.intersectObjects(this.collisionScene.children, true);
      for (const hit of intersects) {
        if (hit.distance <= 0.2) continue;
        if (hit.distance >= finalDistance) break;
        if (!isCameraBlocker(hit.object)) continue;
        finalDistance = Math.max(0.4, hit.distance - 0.2);
        break;
      }
    }

    // Apply clamped distance — reuse scratch vector instead of _rayDir.clone()
    const clampedOffset = this._clampedOffset.copy(_rayDir).multiplyScalar(finalDistance);

    // Smooth lerp to target
    const tppLerp = dt ? 1 - Math.exp(-8 * dt) : 0.15;
    this.currentOffset.lerp(clampedOffset, tppLerp);

    // Final camera position
    this.camera.position.set(
      eyePos.x + this.currentOffset.x,
      eyePos.y + this.currentOffset.y,
      eyePos.z + this.currentOffset.z,
    );

    // Layer the visual recoil overlay into the final camera orientation. The
    // TPP offset math above intentionally used the non-overlay euler so the
    // shoulder/behind distance tracks true aim, not the kicked camera.
    if (this.visualRecoilPitch !== 0 || this.visualRecoilRoll !== 0) {
      this._euler.set(
        this.pitch + this.visualRecoilPitch,
        this.yaw,
        this.visualRecoilRoll,
        'YXZ',
      );
      this.camera.quaternion.setFromEuler(this._euler);
    }
  }

  // ---- Event handlers (private) -------------------------------------------

  private _onMouseMove(e: MouseEvent): void {
    if (!this.locked) return;

    this.yaw -= e.movementX * this.sensitivity;
    this.pitch -= e.movementY * this.sensitivity;
    this.pitch = Math.max(-DEG89, Math.min(DEG89, this.pitch));
  }

  private _onPointerLockChange(): void {
    this.locked = document.pointerLockElement === document.getElementById('game-canvas');
  }

  private _onClick(): void {
    if (!this.locked) {
      this.lock();
    }
  }

}
